import axios from "axios";

// Cache global compartido entre todas las instancias
const globalCache = {
  empleadoTienda: null,
  tiendas: null,
  // Cachés para catálogos pesados
  codigosPostalesOptions: null,
  municipiosOptions: null,
  
  cacheTimestamp: null,
  cacheTimeout: 60000, // 1 minuto (aumentado para catálogos grandes)
  pendingRequests: new Map() // Para evitar requests duplicados simultáneos
};

class CatalogosApiService {
  URL_BASE = process.env.VUE_APP_API_URL + "/catalogos/v1/api";
  URL_BASE_ADMIN = process.env.VUE_APP_API_URL + "/administracion/v1";
  
  constructor() {
    // Usar el cache global compartido
    this.cache = globalCache;
  }

  // Método para verificar si el cache es válido
  isCacheValid() {
    if (!this.cache.cacheTimestamp) return false;
    const now = Date.now();
    const cacheAge = now - this.cache.cacheTimestamp;
    return cacheAge < this.cache.cacheTimeout;
  }

  // Método para limpiar el cache
  clearCache() {
    this.cache.empleadoTienda = null;
    this.cache.tiendas = null;
    // No limpiamos códigos postales y municipios por defecto ya que son muy estáticos
    // y costosos de recargar
    this.cache.cacheTimestamp = null;
    this.cache.pendingRequests.clear();
  }

  // Método específico para forzar recarga total
  forceClearAllCache() {
    this.clearCache();
    this.cache.codigosPostalesOptions = null;
    this.cache.municipiosOptions = null;
  }

  // Bancos
  async getAllBancos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/bancos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllBancosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/bancos/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getBancoById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/bancos/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createBanco(banco) {
    try {
      const response = await axios.post(`${this.URL_BASE}/bancos`, banco);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateBanco(id, banco) {
    try {
      const response = await axios.put(`${this.URL_BASE}/bancos/${id}`, banco);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteBanco(id) {
    try {
      await axios.delete(`${this.URL_BASE}/bancos/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Empleado-Tienda
  async getAllEmpleadoTienda() {
    try {
      // Verificar cache
      if (this.cache.empleadoTienda && this.isCacheValid()) {
        return this.cache.empleadoTienda;
      }

      // Verificar si ya hay una llamada en progreso
      const requestKey = 'getAllEmpleadoTienda';
      if (this.cache.pendingRequests.has(requestKey)) {
        return await this.cache.pendingRequests.get(requestKey);
      }
      // Crear promesa para la llamada
      const requestPromise = axios.get(`${this.URL_BASE_ADMIN}/empleado-tienda/`)
        .then(response => {
          // Actualizar cache
          this.cache.empleadoTienda = response.data;
          this.cache.cacheTimestamp = Date.now();
          
          // Limpiar request pendiente
          this.cache.pendingRequests.delete(requestKey);
          
          return response.data;
        })
        .catch(error => {
          // Limpiar request pendiente en caso de error
          this.cache.pendingRequests.delete(requestKey);
          throw error;
        });

      // Guardar la promesa para evitar llamadas duplicadas
      this.cache.pendingRequests.set(requestKey, requestPromise);
      
      return await requestPromise;
    } catch (error) {
      throw error;
    }
  }

  async getEmpleadoTiendaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/empleado-tienda/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getEmpleadoTiendaByEmpleadoId(empleadoId) {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/empleado-tienda/empleado/${empleadoId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createEmpleadoTienda(empleadoTienda) {
    try {
      const response = await axios.post(`${this.URL_BASE_ADMIN}/empleado-tienda/`, empleadoTienda);
      
      // Invalidar cache después de crear
      this.clearCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateEmpleadoTienda(id, empleadoTienda) {
    try {
      const response = await axios.put(`${this.URL_BASE_ADMIN}/empleado-tienda/${id}`, empleadoTienda);
      
      // Invalidar cache después de actualizar
      this.clearCache();
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteEmpleadoTienda(id) {
    try {
      await axios.delete(`${this.URL_BASE_ADMIN}/empleado-tienda/${id}`);
      
      // Invalidar cache después de eliminar
      this.clearCache();
      return true;
    } catch (error) {
      throw error;
    }
  }

  // Obtener empleados para el select
  async getEmpleadosOptions() {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/empleados/options`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Obtener tiendas para el select
  async getTiendasOptions() {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/tienda/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Areas
  async getAllAreas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/areas`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllAreasActivas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/areas/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAreaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/areas/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createArea(area) {
    try {
      const response = await axios.post(`${this.URL_BASE}/areas`, area);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateArea(id, area) {
    try {
      const response = await axios.put(`${this.URL_BASE}/areas/${id}`, area);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteArea(id) {
    try {
      await axios.delete(`${this.URL_BASE}/areas/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Clientes
  async getAllClientes() {
    try {
      const response = await axios.get(`${this.URL_BASE}/clientes`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllClientesActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/clientes/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getClienteById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/clientes/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createCliente(cliente) {
    try {
      const response = await axios.post(`${this.URL_BASE}/clientes`, cliente);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateCliente(id, cliente) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/clientes/${id}`,
        cliente
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteCliente(id) {
    try {
      await axios.delete(`${this.URL_BASE}/clientes/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Códigos Postales
  async getAllCodigosPostales() {
    try {
      const response = await axios.get(`${this.URL_BASE}/codigos-postales`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllCodigosPostalesActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/codigos-postales/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getCodigoPostalById(id) {
    try {
      const response = await axios.get(
        `${this.URL_BASE}/codigos-postales/${id}`
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createCodigoPostal(codigoPostal) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/codigos-postales`,
        codigoPostal
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateCodigoPostal(id, codigoPostal) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/codigos-postales/${id}`,
        codigoPostal
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteCodigoPostal(id) {
    try {
      await axios.delete(`${this.URL_BASE}/codigos-postales/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Colonias
  async getAllColonias() {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createColonia(colonia) {
    try {
      const response = await axios.post(`${this.URL_BASE}/colonias`, colonia);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateColonia(id, colonia) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/colonias/${id}`,
        colonia
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteColonia(id) {
    try {
      await axios.delete(`${this.URL_BASE}/colonias/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  async getColoniasPorCodigoPostal(codigoPostal) {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/por-codigo-postal/${codigoPostal}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Métodos paginados para colonias

  // Método wrapper para CatalogCrud que devuelve los datos con información de paginación
  async getAllColonias(page = 0, size = 10, sortBy = 'nombre', sortDir = 'asc') {
    try {

      const response = await this.getAllColoniasPaginadas(page, size, sortBy, sortDir);

      return response; // Devolver la respuesta completa con información de paginación
    } catch (error) {

      throw error;
    }
  }

  // Método específico para obtener todas las colonias paginadas (para el nuevo sistema)
  async getAllColoniasPaginadas(page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {
      const url = `${this.URL_BASE}/colonias/paginadas/activas`;

      const response = await axios.get(url, {
        params: { page, size, sortBy, sortDir }
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniasActivasPaginadas(page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas/activas`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async buscarColoniasPorNombre(nombre, page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {

      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas/buscar`, {
        params: { nombre, page, size, sortBy, sortDir }
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async buscarColoniasConFiltros(filtros) {
    try {

      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas/filtros`, {
        params: filtros
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniasFiltradas(estatus, page = 0, size = 10, filters = {}) {
    try {

      const response = await axios.get(`${this.URL_BASE}/colonias/coloniasFiltradas/${estatus}`, {
        params: { page, size, ...filters }
      });

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniasPorCodigoPostalPaginadas(codigoPostal, page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas/por-codigo-postal/${codigoPostal}`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Departamentos
  async getAllDepartamentos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/departamentos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllDepartamentosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/departamentos/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getDepartamentoById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/departamentos/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createDepartamento(departamento) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/departamentos`,
        departamento
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateDepartamento(id, departamento) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/departamentos/${id}`,
        departamento
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteDepartamento(id) {
    try {
      await axios.delete(`${this.URL_BASE}/departamentos/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Empresas
  async getAllEmpresas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/empresas`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllEmpresasActivas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/empresas/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getEmpresaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/empresas/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createEmpresa(empresa) {
    try {
      const response = await axios.post(`${this.URL_BASE}/empresas`, empresa);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateEmpresa(id, empresa) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/empresas/${id}`,
        empresa
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteEmpresa(id) {
    try {
      await axios.delete(`${this.URL_BASE}/empresas/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Empleado Cuenta Bancaria
  async getAllEmpleadoCuentasBancarias() {
    try {
      const response = await axios.get(
        `${this.URL_BASE}/empleado-cuenta-bancaria`
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getEmpleadoCuentaBancariaById(id) {
    try {
      const response = await axios.get(
        `${this.URL_BASE}/empleado-cuenta-bancaria/${id}`
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createEmpleadoCuentaBancaria(cuentaBancaria) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/empleado-cuenta-bancaria`,
        cuentaBancaria
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateEmpleadoCuentaBancaria(id, cuentaBancaria) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/empleado-cuenta-bancaria/${id}`,
        cuentaBancaria
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteEmpleadoCuentaBancaria(id) {
    try {
      await axios.delete(`${this.URL_BASE}/empleado-cuenta-bancaria/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Opciones para selects
  async getBancoOptions() {
    try {
      const bancos = await this.getAllBancosActivos();
      return bancos.map((banco) => ({
        label: banco.nombre,
        value: banco.id,
      }));
    } catch (error) {

      throw error;
    }
  }



  // Estados
  async getAllEstados() {
    try {
      const response = await axios.get(`${this.URL_BASE}/estados/findAll`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllEstadosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/estados/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createEstado(estado) {
    try {
      const response = await axios.post(`${this.URL_BASE}/estados`, estado);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateEstado(id, estado) {
    try {
      const response = await axios.put(`${this.URL_BASE}/estados/${id}`, estado);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Esquemas
  async getAllEsquemas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/esquemas/findAll`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllEsquemasActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/esquemas/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createEsquema(esquema) {
    try {
      // Validar que tipoEsquema no sea nulo o vacío
      if (!esquema.tipoEsquema || esquema.tipoEsquema.trim() === "") {
        throw new Error(
          "El campo 'tipoEsquema' es obligatorio y no puede estar vacío."
        );
      }

      const response = await axios.post(`${this.URL_BASE}/esquemas`, esquema);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateEsquema(id, esquema) {
    try {
      // Validar que tipoEsquema no sea nulo o vacío
      if (!esquema.tipoEsquema || esquema.tipoEsquema.trim() === "") {
        throw new Error(
          "El campo 'tipoEsquema' es obligatorio y no puede estar vacío."
        );
      }

      const response = await axios.put(`${this.URL_BASE}/esquemas/${id}`, esquema);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteEsquema(id) {
    try {
      await axios.delete(`${this.URL_BASE}/esquemas/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Municipios
  async getAllMunicipios() {
    try {
      const response = await axios.get(`${this.URL_BASE}/municipios/findAll`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllMunicipiosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/municipios/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createMunicipio(municipio) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/municipios`,
        municipio
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateMunicipio(id, municipio) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/municipios/${id}`,
        municipio
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Países
  async getAllPaises() {
    try {
      const response = await axios.get(`${this.URL_BASE}/paises/findAll`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllPaisesActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/paises/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createPais(pais) {
    try {
      const response = await axios.post(`${this.URL_BASE}/paises`, pais);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updatePais(id, pais) {
    try {
      const response = await axios.put(`${this.URL_BASE}/paises/${id}`, pais);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Periodos de Pago
  async getAllPeriodosPago() {
    try {
      const response = await axios.get(`${this.URL_BASE}/periodos-pago`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllPeriodosPagoActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/periodos-pago/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createPeriodoPago(periodoPago) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/periodos-pago`,
        periodoPago
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updatePeriodoPago(id, periodoPago) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/periodos-pago/${id}`,
        periodoPago
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Puestos
  async getAllPuestos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/puestos/findAll`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllPuestosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/puestos/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createPuesto(puesto) {
    try {
      const response = await axios.post(`${this.URL_BASE}/puestos`, puesto);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updatePuesto(id, puesto) {
    try {
      const response = await axios.put(`${this.URL_BASE}/puestos/${id}`, puesto);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Tabuladores
  async getAllTabuladores() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tabuladores/findAll`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllTabuladoresActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tabuladores/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createTabulador(tabulador) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/tabuladores`,
        tabulador
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateTabulador(id, tabulador) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/tabuladores/${id}`,
        tabulador
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Tiendas
  async getAllTiendas() {
    try {
      // Verificar cache
      if (this.cache.tiendas && this.isCacheValid()) {
        return this.cache.tiendas;
      }

      // Verificar si ya hay una llamada en progreso
      const requestKey = 'getAllTiendas';
      if (this.cache.pendingRequests.has(requestKey)) {
        return await this.cache.pendingRequests.get(requestKey);
      }
      // Crear promesa para la llamada
      const requestPromise = axios.get(`${this.URL_BASE}/tiendas/findAll`)
        .then(response => {
          // Actualizar cache
          this.cache.tiendas = response.data;
          this.cache.cacheTimestamp = Date.now();
          
          // Limpiar request pendiente
          this.cache.pendingRequests.delete(requestKey);
          
          return response.data;
        })
        .catch(error => {
          // Limpiar request pendiente en caso de error
          this.cache.pendingRequests.delete(requestKey);
          throw error;
        });

      // Guardar la promesa para evitar llamadas duplicadas
      this.cache.pendingRequests.set(requestKey, requestPromise);
      
      return await requestPromise;
    } catch (error) {

      throw error;
    }
  }

  async createTienda(tienda) {
    try {
      const response = await axios.post(`${this.URL_BASE}/tiendas`, tienda);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getTiendaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/tiendas/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateTienda(id, tienda) {
    try {
      const response = await axios.put(`${this.URL_BASE}/tiendas/${id}`, tienda);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteTienda(id) {
    try {
      const response = await axios.delete(`${this.URL_BASE}/tiendas/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Tipos de Tabulador
  async getAllTiposTabulador() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-tabulador`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllTiposTabuladorActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-tabulador/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createTipoTabulador(tipoTabulador) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/tipos-tabulador`,
        tipoTabulador
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateTipoTabulador(id, tipoTabulador) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/tipos-tabulador/${id}`,
        tipoTabulador
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Tipos de Tienda
  async getAllTiposTienda() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-tienda`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllTiposTiendaActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-tienda/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createTipoTienda(tipoTienda) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/tipos-tienda`,
        tipoTienda
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateTipoTienda(id, tipoTienda) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/tipos-tienda/${id}`,
        tipoTienda
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Tipos de Zona Tabulador
  async getAllTiposZonaTabulador() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-zona-tabulador`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllTiposZonaTabuladorActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-zona-tabulador/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createTipoZonaTabulador(tipoZonaTabulador) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/tipos-zona-tabulador`,
        tipoZonaTabulador
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateTipoZonaTabulador(id, tipoZonaTabulador) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/tipos-zona-tabulador/${id}`,
        tipoZonaTabulador
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // Unidades de Flota
  async getAllUnidadesFlota() {
    try {
      const response = await axios.get(`${this.URL_BASE}/unidades-flota`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createUnidadFlota(unidadFlota) {
    try {
      const response = await axios.post(
        `${this.URL_BASE}/unidades-flota`,
        unidadFlota
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getCodigoPostalOptions() {
    try {
      // Verificar caché
      if (this.cache.codigosPostalesOptions) {
        return this.cache.codigosPostalesOptions;
      }

      const codigosPostales = await this.getAllCodigosPostalesActivos();
      const options = codigosPostales.map((codigoPostal) => ({
        label: codigoPostal.codigo_postal,
        value: codigoPostal.id_codigo_postal,
      }));

      // Guardar en caché
      this.cache.codigosPostalesOptions = options;
      return options;
    } catch (error) {

      throw error;
    }
  }

  async getMunicipioOptions() {
    try {
      // Verificar caché
      if (this.cache.municipiosOptions) {
        return this.cache.municipiosOptions;
      }

      const municipios = await this.getAllMunicipiosActivos();
      const options = municipios.map((municipio) => ({
        label: municipio.nombre || "Sin nombre",
        value: municipio.id, // Asignar siempre id_municipio como valor
      }));

      // Guardar en caché
      this.cache.municipiosOptions = options;
      return options;
    } catch (error) {

      throw error;
    }
  }

  async getEstadoOptions() {
    try {
      const estados = await this.getAllEstadosActivos();
      return estados.map((estado) => ({
        label: estado.estado,
        value: estado.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  // Ciudades
  async getCiudades() {
    try {
      const response = await axios.get(`${this.URL_BASE}/ciudades`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createCiudad(ciudad) {
    try {
      const response = await axios.post(`${this.URL_BASE}/ciudades`, ciudad);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateCiudad(id, ciudad) {
    try {
      const response = await axios.put(
        `${this.URL_BASE}/ciudades/${id}`,
        ciudad
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteCiudad(id) {
    try {
      await axios.delete(`${this.URL_BASE}/ciudades/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  async getCiudadOptions() {
    try {
      const ciudades = await this.getCiudades();
      return ciudades.map((ciudad) => ({
        label: ciudad.ciudad || "Sin nombre",
        value: ciudad.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  // Formatos
  async getAllFormatos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/formatos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllFormatosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/formatos/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getFormatoById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/formatos/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createFormato(formato) {
    try {
      const response = await axios.post(`${this.URL_BASE}/formatos`, formato);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateFormato(id, formato) {
    try {
      const response = await axios.put(`${this.URL_BASE}/formatos/${id}`, formato);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteFormato(id) {
    try {
      await axios.delete(`${this.URL_BASE}/formatos/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Proyectos
  async getAllProyectos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/proyectos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllProyectosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/proyectos/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getProyectoById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/proyectos/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createProyecto(proyecto) {
    try {
      const response = await axios.post(`${this.URL_BASE}/proyectos`, proyecto);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateProyecto(id, proyecto) {
    try {
      const response = await axios.put(`${this.URL_BASE}/proyectos/${id}`, proyecto);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteProyecto(id) {
    try {
      await axios.delete(`${this.URL_BASE}/proyectos/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Proveedores
  async getAllProveedores() {
    try {
      const response = await axios.get(`${this.URL_BASE}/proveedores`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getProveedorById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/proveedores/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createProveedor(proveedor) {
    try {
      const response = await axios.post(`${this.URL_BASE}/proveedores`, proveedor);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateProveedor(id, proveedor) {
    try {
      const response = await axios.put(`${this.URL_BASE}/proveedores/${id}`, proveedor);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteProveedor(id) {
    try {
      await axios.delete(`${this.URL_BASE}/proveedores/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Marcas
  async getAllMarcas() {
    try {

      const response = await axios.get(`${this.URL_BASE}/marcas`);

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllMarcasActivas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/marcas/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getMarcaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/marcas/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createMarca(marca) {
    try {
      const response = await axios.post(`${this.URL_BASE}/marcas`, marca);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateMarca(id, marca) {
    try {
      const response = await axios.put(`${this.URL_BASE}/marcas/${id}`, marca);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteMarca(id) {
    try {
      await axios.delete(`${this.URL_BASE}/marcas/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Submarcas
  async getAllSubmarcas() {
    try {

      const response = await axios.get(`${this.URL_BASE}/submarcas`);

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllSubmarcasActivas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/submarcas/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getSubmarcaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/submarcas/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getSubmarcasByMarca(idMarca) {
    try {
      const response = await axios.get(`${this.URL_BASE}/submarcas/marca/${idMarca}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createSubmarca(submarca) {
    try {
      const response = await axios.post(`${this.URL_BASE}/submarcas`, submarca);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateSubmarca(id, submarca) {
    try {
      const response = await axios.put(`${this.URL_BASE}/submarcas/${id}`, submarca);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteSubmarca(id) {
    try {
      await axios.delete(`${this.URL_BASE}/submarcas/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  async getMarcaOptions() {
    try {
      const marcas = await this.getAllMarcasActivas();
      return marcas.map((marca) => ({
        label: marca.nombre || "Sin nombre",
        value: marca.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  // Años
  async getAllAnios() {
    try {

      const response = await axios.get(`${this.URL_BASE}/anios`);

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllAniosActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/anios/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAnioById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/anios/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createAnio(anio) {
    try {
      const response = await axios.post(`${this.URL_BASE}/anios`, anio);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateAnio(id, anio) {
    try {
      const response = await axios.put(`${this.URL_BASE}/anios/${id}`, anio);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteAnio(id) {
    try {
      await axios.delete(`${this.URL_BASE}/anios/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Submarca-Años
  async getAllSubmarcaAnios() {
    try {
      const response = await axios.get(`${this.URL_BASE}/submarca-anios`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getSubmarcaAnioById(idSubmarca, idAnio) {
    try {
      const response = await axios.get(`${this.URL_BASE}/submarca-anios/${idSubmarca}/${idAnio}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getSubmarcaAniosBySubmarca(idSubmarca) {
    try {

      const response = await axios.get(`${this.URL_BASE}/submarca-anios/submarca/${idSubmarca}`);

      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getSubmarcaAniosByAnio(idAnio) {
    try {
      const response = await axios.get(`${this.URL_BASE}/submarca-anios/anio/${idAnio}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createSubmarcaAnio(submarcaAnio) {
    try {
      const response = await axios.post(`${this.URL_BASE}/submarca-anios`, submarcaAnio);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateSubmarcaAnio(idSubmarca, idAnio, submarcaAnio) {
    try {
      const response = await axios.put(`${this.URL_BASE}/submarca-anios/${idSubmarca}/${idAnio}`, submarcaAnio);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteSubmarcaAnio(idSubmarca, idAnio) {
    try {
      await axios.delete(`${this.URL_BASE}/submarca-anios/${idSubmarca}/${idAnio}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  async getAnioOptions() {
    try {
      const anios = await this.getAllAniosActivos();
      return anios.map((anio) => ({
        label: anio.anio.toString(),
        value: anio.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  async getSubmarcaOptions() {
    try {
      const submarcas = await this.getAllSubmarcasActivas();
      return submarcas.map((submarca) => ({
        label: `${submarca.nombre} (${submarca.nombreMarca})`,
        value: submarca.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  // Esquemas Unidad
  async getAllEsquemasUnidad() {
    try {
      const response = await axios.get(`${this.URL_BASE}/esquemas-unidad`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllEsquemasUnidadActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/esquemas-unidad/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getEsquemaUnidadById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/esquemas-unidad/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createEsquemaUnidad(esquemaUnidad) {
    try {
      const response = await axios.post(`${this.URL_BASE}/esquemas-unidad`, esquemaUnidad);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateEsquemaUnidad(id, esquemaUnidad) {
    try {
      const response = await axios.put(`${this.URL_BASE}/esquemas-unidad/${id}`, esquemaUnidad);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteEsquemaUnidad(id) {
    try {
      await axios.delete(`${this.URL_BASE}/esquemas-unidad/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Aseguradoras
  async getAllAseguradoras() {
    try {
      const response = await axios.get(`${this.URL_BASE}/aseguradoras`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllAseguradorasActivas() {
    try {
      const response = await axios.get(`${this.URL_BASE}/aseguradoras/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAseguradoraById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/aseguradoras/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createAseguradora(aseguradora) {
    try {
      const response = await axios.post(`${this.URL_BASE}/aseguradoras`, aseguradora);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateAseguradora(id, aseguradora) {
    try {
      const response = await axios.put(`${this.URL_BASE}/aseguradoras/${id}`, aseguradora);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteAseguradora(id) {
    try {
      await axios.delete(`${this.URL_BASE}/aseguradoras/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Colores
  async getAllColores() {
    try {
      const response = await axios.get(`${this.URL_BASE}/colores`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllColoresActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/colores/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColorById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/colores/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createColor(color) {
    try {
      const response = await axios.post(`${this.URL_BASE}/colores`, color);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateColor(id, color) {
    try {
      const response = await axios.put(`${this.URL_BASE}/colores/${id}`, color);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteColor(id) {
    try {
      await axios.delete(`${this.URL_BASE}/colores/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Tipos de Vehículo
  async getAllTiposVehiculo() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-vehiculo`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllTiposVehiculoActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-vehiculo/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getTipoVehiculoById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-vehiculo/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createTipoVehiculo(tipoVehiculo) {
    try {
      const response = await axios.post(`${this.URL_BASE}/tipos-vehiculo`, tipoVehiculo);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateTipoVehiculo(id, tipoVehiculo) {
    try {
      const response = await axios.put(`${this.URL_BASE}/tipos-vehiculo/${id}`, tipoVehiculo);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteTipoVehiculo(id) {
    try {
      await axios.delete(`${this.URL_BASE}/tipos-vehiculo/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  async getColorOptions() {
    try {
      const colores = await this.getAllColoresActivos();
      return colores.map((color) => ({
        label: color.nombre,
        value: color.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  async getTipoVehiculoOptions() {
    try {
      const tiposVehiculo = await this.getAllTiposVehiculoActivos();
      return tiposVehiculo.map((tipo) => ({
        label: tipo.nombre,
        value: tipo.id,
      }));
    } catch (error) {

      throw error;
    }
  }

  // Roles de Asignación
  async getAllRolesAsignacion() {
    try {
      const response = await axios.get(`${this.URL_BASE}/roles-asignacion`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllRolesAsignacionActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/roles-asignacion/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getRolAsignacionById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/roles-asignacion/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createRolAsignacion(rolAsignacion) {
    try {
      const response = await axios.post(`${this.URL_BASE}/roles-asignacion`, rolAsignacion);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateRolAsignacion(id, rolAsignacion) {
    try {
      const response = await axios.put(`${this.URL_BASE}/roles-asignacion/${id}`, rolAsignacion);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteRolAsignacion(id) {
    try {
      await axios.delete(`${this.URL_BASE}/roles-asignacion/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Tipos de Servicio de Asignación
  async getAllServiciosAsignacion() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-servicio-asignacion`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllServiciosAsignacionActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-servicio-asignacion/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getServicioAsignacionById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/tipos-servicio-asignacion/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createServicioAsignacion(servicioAsignacion) {
    try {
      const response = await axios.post(`${this.URL_BASE}/tipos-servicio-asignacion`, servicioAsignacion);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateServicioAsignacion(id, servicioAsignacion) {
    try {
      const response = await axios.put(`${this.URL_BASE}/tipos-servicio-asignacion/${id}`, servicioAsignacion);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteServicioAsignacion(id) {
    try {
      await axios.delete(`${this.URL_BASE}/tipos-servicio-asignacion/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Estatus de Operación de Asignación
  async getAllEstatusOperacion() {
    try {
      const response = await axios.get(`${this.URL_BASE}/estatus-operacion-asignacion`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllEstatusOperacionActivos() {
    try {
      const response = await axios.get(`${this.URL_BASE}/estatus-operacion-asignacion/activos`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getEstatusOperacionById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/estatus-operacion-asignacion/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createEstatusOperacion(estatusOperacion) {
    try {
      const response = await axios.post(`${this.URL_BASE}/estatus-operacion-asignacion`, estatusOperacion);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateEstatusOperacion(id, estatusOperacion) {
    try {
      const response = await axios.put(`${this.URL_BASE}/estatus-operacion-asignacion/${id}`, estatusOperacion);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteEstatusOperacion(id) {
    try {
      await axios.delete(`${this.URL_BASE}/estatus-operacion-asignacion/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  // Usuario Rol
  async getAllUsuarioRoles() {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/usuario-rol`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getUsuarioRolById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/usuario-rol/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createUsuarioRol(usuarioRol) {
    try {
      const response = await axios.post(`${this.URL_BASE_ADMIN}/usuario-rol`, usuarioRol);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateUsuarioRol(id, usuarioRol) {
    try {
      const response = await axios.put(`${this.URL_BASE_ADMIN}/usuario-rol/${id}`, usuarioRol);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async deleteUsuarioRol(id) {
    try {
      await axios.delete(`${this.URL_BASE_ADMIN}/usuario-rol/${id}`);
      return true;
    } catch (error) {

      throw error;
    }
  }

  async getEmpleadoOptions(sistemaId = null) {
    try {
      // Intentar primero con el endpoint de options que es más directo
      let url = `${this.URL_BASE_ADMIN}/empleados/options`;
      if (sistemaId) {
        url += `?sistemaId=${sistemaId}`;
      }
      
      const response = await axios.get(url);
      
      // Si el endpoint /options devuelve datos, los usamos
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        return response.data;
      }
      
      // Fallback: Obtenemos los empleados activos y los mapeamos manualmente
      // Usamos el endpoint de empleados activos del microservicio de administración
      const timestamp = new Date().getTime();
      const urlFallback = `${this.URL_BASE_ADMIN}/empleado/empleadosActivos/1?page=0&size=100000&_t=${timestamp}`;
      const responseFallback = await axios.get(urlFallback);
      
      // response.data es Page<Empleado> o un Array
      let empleados = [];
      if (responseFallback.data.content && Array.isArray(responseFallback.data.content)) {
        empleados = responseFallback.data.content;
      } else if (Array.isArray(responseFallback.data)) {
        empleados = responseFallback.data;
      }
      
      return empleados.map(emp => {
        const id = emp.id || emp.idEmpleado || emp.empId || emp.numEmpleado || emp.num_empleado;
        const numero = emp.numEmpleado || emp.numeroEmpleado || emp.num_empleado || emp.empId || '';
        const nombre = emp.empNombre || emp.nombre || emp.nombres || '';
        const paterno = emp.apellidoPaterno || emp.apellido_paterno || '';
        const materno = emp.apellidoMaterno || emp.apellido_materno || '';
        
        const nombreCompleto = `${nombre} ${paterno} ${materno}`.replaceAll(/\s+/g, ' ').trim().toUpperCase();
        
        return {
            label: numero ? `${numero} - ${nombreCompleto}` : nombreCompleto,
            value: id,
            numEmpleado: numero
        };
      });
    } catch (error) {

      return [];
    }
  }

  async getRolOptions(sistemaId = null) {
    try {
      if (!sistemaId) {
        return [];
      }
      const response = await axios.get(`${this.URL_BASE}/roles/sistema/${sistemaId}`);
      // Verificar que response.data sea un array
      if (!Array.isArray(response.data)) {
        return [];
      }
      // Transformar la respuesta del endpoint de catálogos al formato esperado (value/label)
      const transformedData = response.data.map(rol => {
        return {
          value: rol.id,
          label: rol.rol
        };
      });
      return transformedData;
    } catch (error) {
      throw error;
    }
  }

  async getSistemaOptions() {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/sistemas/options`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Colonias
  async getAllColonias() {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAllColoniasPaginadas(page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniasActivasPaginadas(page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas/activas`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniasInactivasPaginadas(page = 0, size = 20, sortBy = 'nombre', sortDir = 'asc') {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/paginadas/inactivas`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getColoniaById(id) {
    try {
      const response = await axios.get(`${this.URL_BASE}/colonias/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async createColonia(colonia) {
    try {
      const response = await axios.post(`${this.URL_BASE}/colonias`, colonia);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async updateColonia(id, colonia) {
    try {
      const response = await axios.put(`${this.URL_BASE}/colonias/${id}`, colonia);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getAccesoOptions() {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/accesos/options`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSubaccesoOptions() {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/subaccesos/options`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAccionesBySubacceso(subaccesoId) {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/acciones/subacceso/${subaccesoId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAccionesBySistema() {
    try {
      // Usamos el endpoint que lista todas las acciones activas del sistema (configurado en el controlador de roles)
      const response = await axios.get(`${this.URL_BASE_ADMIN}/acciones-rol/acciones`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Accesos Especiales
  async getAllAccesosEspeciales(numEmpleado) {
    try {
      const response = await axios.get(`${this.URL_BASE_ADMIN}/accesos-especiales/empleado/${numEmpleado}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createAccesoEspecial(accesoEspecial) {
    try {
      const response = await axios.post(`${this.URL_BASE_ADMIN}/accesos-especiales/asignar`, accesoEspecial);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteAccesoEspecial(id) {
    try {
      await axios.delete(`${this.URL_BASE_ADMIN}/accesos-especiales/${id}`);
      return true;
    } catch (error) {
      throw error;
    }
  }
}

// Singleton pattern para asegurar una sola instancia
let instance = null;

function getCatalogosApiService() {
  if (!instance) {
    instance = new CatalogosApiService();
  } else {
  }
  return instance;
}

// Exportar tanto la función como la clase
export { getCatalogosApiService, CatalogosApiService };
export default getCatalogosApiService;
